import { LayoutGraph, PortPositionsMap, PositionUpdate } from './types';
import { findAutonomousRoots } from './findAutonomousRoots';
import { assignLevels } from './assignLevels';
import { calculateLevelWidths } from './calculateLevelWidths';
import { calculateXPositions } from './calculateXPositions';
import { determineStackingOrder } from './determineStackingOrder';
import { calculateYPositions } from './calculateYPositions';

const AUTONOMOUS_GROUP_VERTICAL_MARGIN = 100;

/**
 * Main function that coordinates the graph layout algorithm.
 *
 * Takes a graph and node IDs to layout, and returns new positions for all affected nodes.
 *
 * Algorithm steps:
 * 1. Find autonomous root sets (groups of roots with non-overlapping descendants)
 * 2. For each autonomous root set:
 *    a. Assign levels starting from roots at level 0
 *    b. Calculate width of each level
 *    c. Calculate X positions (roots on right, moving left)
 *    d. Determine stacking order within each level
 *    e. Calculate Y positions (stack tallest level, then propagate)
 * 3. Offset autonomous groups vertically with margin
 * 4. Return position updates
 */
export function layoutGraph(graph: LayoutGraph, nodeIdsToLayout: number[], portPositions?: PortPositionsMap): PositionUpdate[] {
    if (nodeIdsToLayout.length === 0) {
        return [];
    }

    // Step 1: Find autonomous root sets
    const autonomousRootSets = findAutonomousRoots(graph, nodeIdsToLayout);

    // Step 2: Process each autonomous root set
    const groupPositions: Map<number, { x: number; y: number }>[] = [];
    const groupHeights: number[] = [];

    for (const rootSet of autonomousRootSets) {
        const positions = new Map<number, { x: number; y: number }>();

        // Step 2a: Assign levels
        const levels = assignLevels(graph, rootSet.rootIds, rootSet.nodeIds);

        // Step 2b: Calculate level widths
        const levelWidths = calculateLevelWidths(graph, levels);

        // Step 2c: Calculate X positions
        const xPositions = calculateXPositions(graph, levels, levelWidths);

        // Step 2d: Determine stacking order
        const stackingOrder = determineStackingOrder(graph, levels);

        // Step 2e: Calculate Y positions
        const yPositions = calculateYPositions(graph, levels, stackingOrder, portPositions);

        // Combine X and Y positions
        for (const nodeId of rootSet.nodeIds) {
            const x = xPositions.get(nodeId) ?? 0;
            const y = yPositions.get(nodeId) ?? 0;
            positions.set(nodeId, { x, y });
        }

        // Calculate height of this group (max Y + node height)
        let maxBottom = 0;
        const nodeMap = new Map(graph.nodes.map(node => [node.id, node]));

        for (const nodeId of rootSet.nodeIds) {
            const pos = positions.get(nodeId)!;
            const node = nodeMap.get(nodeId)!;
            const bottom = pos.y + node.height;
            maxBottom = Math.max(maxBottom, bottom);
        }

        groupPositions.push(positions);
        groupHeights.push(maxBottom);
    }

    // Step 3: Offset autonomous groups vertically
    let cumulativeYOffset = 0;
    const finalPositions: PositionUpdate[] = [];

    for (let i = 0; i < groupPositions.length; i++) {
        const positions = groupPositions[i];

        for (const [nodeId, pos] of positions.entries()) {
            finalPositions.push({
                nodeId,
                x: pos.x,
                y: pos.y + cumulativeYOffset,
            });
        }

        cumulativeYOffset += groupHeights[i] + AUTONOMOUS_GROUP_VERTICAL_MARGIN;
    }

    return finalPositions;
}
